// Conviction-line chart geometry + timing (from consensus-builds prototype).

const CONSENSUS_CHART_X_SOURCE = [70, 170, 270, 371, 471, 571, 672, 772] as const;

/** Compresses horizontal span so the 1.15× stage scale fits without clipping. */
export const CONSENSUS_CHART_HORIZONTAL_SCALE = 0.82 as const;

const CONSENSUS_CHART_X_START: number = CONSENSUS_CHART_X_SOURCE[0];

export const CONSENSUS_CHART_X: readonly number[] = CONSENSUS_CHART_X_SOURCE.map(
  (x) => CONSENSUS_CHART_X_START + (x - CONSENSUS_CHART_X_START) * CONSENSUS_CHART_HORIZONTAL_SCALE,
);

export const CONSENSUS_CHART_X_MAX: number = CONSENSUS_CHART_X[CONSENSUS_CHART_X.length - 1] ?? 772;

export const CONSENSUS_CHART_VIEW_WIDTH: number =
  CONSENSUS_CHART_X_MAX - CONSENSUS_CHART_X_START + 90;

export const CONSENSUS_CHART_STAGE_SCALE = 1.15 as const;

/** Horizontal target band in SVG coords (lower y = higher on chart). */
export const CONSENSUS_CHART_TARGET_Y = 216 as const;

/** Loser lines stay this far below the target centerline so stroke never kisses it. */
export const CONSENSUS_CHART_LOSER_MIN_Y: number = CONSENSUS_CHART_TARGET_Y + 8;

export const CONSENSUS_CHART_SHAPES = {
  // Lime — volatile swings, finishes lowest of the losers.
  spike: [232, 252, 224, 260, 228, 278, 305, 322],
  // Orange — moderate drift, never crosses target.
  mid: [232, 228, 232, 226, 238, 252, 272, 292],
  win: [232, 280, 250, 300, 232, 200, 168, 150],
} as const;

export type ConsensusChartLineKey = keyof typeof CONSENSUS_CHART_SHAPES;

export const CONSENSUS_CHART_TIMING = {
  revealMs: 2600,
  crossAt: 0.7,
  dimLosersAfterMs: 260,
  sealAfterMs: 420,
  intersectionThreshold: 0.45,
} as const;

export function easeInOutCubic(progress: number): number {
  return progress < 0.5 ? 4 * progress * progress * progress : 1 - (-2 * progress + 2) ** 3 / 2;
}

/** Reveal animation through seal stamp (consensus-builds-chart playback). */
export function getConsensusChartPlaybackDurationMs(): number {
  const { revealMs, sealAfterMs } = CONSENSUS_CHART_TIMING;
  return revealMs + sealAfterMs;
}

/** Catmull-Rom → cubic Bézier through the sample points. */
export function buildSmoothPath(xs: readonly number[], ys: readonly number[]): string {
  const points = xs.map((x, index) => [x, ys[index] ?? 0] as const);
  let path = `M ${points[0]?.[0] ?? 0} ${points[0]?.[1] ?? 0}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const previous = points[index - 1] ?? points[index];
    const current = points[index];
    const next = points[index + 1];
    const after = points[index + 2] ?? next;
    if (
      current === undefined ||
      next === undefined ||
      previous === undefined ||
      after === undefined
    ) {
      continue;
    }
    const control1x = current[0] + (next[0] - previous[0]) / 6;
    const control1y = current[1] + (next[1] - previous[1]) / 6;
    const control2x = next[0] - (after[0] - current[0]) / 6;
    const control2y = next[1] - (after[1] - current[1]) / 6;
    path += ` C ${control1x} ${control1y} ${control2x} ${control2y} ${next[0]} ${next[1]}`;
  }
  return path;
}

export function buildSealPath(
  cx: number,
  cy: number,
  radius: number = 17,
  bumps: number = 11,
): string {
  let path = '';
  const segments = bumps * 2;
  for (let index = 0; index <= segments; index += 1) {
    const angle = (index / segments) * Math.PI * 2 - Math.PI / 2;
    const scallopRadius = index % 2 === 0 ? radius : radius * 0.84;
    const x = cx + Math.cos(angle) * scallopRadius;
    const y = cy + Math.sin(angle) * scallopRadius;
    path += `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)} `;
  }
  return `${path}Z`;
}

export function buildSealCheckPath(cx: number, cy: number): string {
  return `M ${cx - 5} ${cy + 0.5} L ${cx - 1} ${cy + 4.5} L ${cx + 5.5} ${cy - 5}`;
}

/** Rough polyline length when `SVGPathElement.getTotalLength` is unavailable (jsdom, SSR). */
export function estimatePolylineLength(xs: readonly number[], ys: readonly number[]): number {
  let length = 0;
  for (let index = 1; index < xs.length; index += 1) {
    const dx = (xs[index] ?? 0) - (xs[index - 1] ?? 0);
    const dy = (ys[index] ?? 0) - (ys[index - 1] ?? 0);
    length += Math.hypot(dx, dy);
  }
  return length * 1.15;
}

export function samplePolylinePoint(
  xs: readonly number[],
  ys: readonly number[],
  fraction: number,
): { x: number; y: number } {
  const clamped = Math.min(Math.max(fraction, 0), 1);
  const position = clamped * (xs.length - 1);
  const low = Math.floor(position);
  const high = Math.min(low + 1, xs.length - 1);
  const t = position - low;
  const x0 = xs[low] ?? 0;
  const x1 = xs[high] ?? x0;
  const y0 = ys[low] ?? 0;
  const y1 = ys[high] ?? y0;
  return { x: x0 + (x1 - x0) * t, y: y0 + (y1 - y0) * t };
}

export function canMeasureSvgPath(path: SVGPathElement): boolean {
  return typeof path.getTotalLength === 'function' && typeof path.getPointAtLength === 'function';
}
